import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Target, BarChart3, HelpCircle, MapPin, Loader2, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConsultationDialog from "@/components/ConsultationDialog";
import { Reveal } from "@/lib/motion";
import { getIndustryPage, listIndustryPages } from "@/lib/api";

function IndustryIndex() {
  const [pages, setPages] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Industry Lead Generation | WebsiteTech.pro";
    listIndustryPages().then(setPages).catch(() => {});
  }, []);
  return (
    <>
      <Navbar />
      <main className="pt-24" data-testid="industry-index">
        <section className="hero-bg border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
            <span className="overline">Industry Lead Generation</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mt-3 max-w-3xl">
              Lead generation, tailored to your industry & city
            </h1>
            <p className="text-lg text-slate-500 mt-6 max-w-2xl">
              Playbooks built around how customers actually buy in your industry and location — the channels, the funnel and the KPIs that matter.
            </p>
          </div>
        </section>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pages.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.05}>
                <Link to={`/industries/${p.slug}`} data-testid={`industry-card-${p.slug}`}
                  className="group block h-full rounded-2xl border border-slate-200 bg-white p-6 card-shadow hover-lift">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-brand">
                    <MapPin size={13} /> {p.location}
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-500">{p.hero_subtitle}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                    View playbook <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
            {pages.length === 0 && <p className="text-slate-400">No industry pages published yet.</p>}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function IndustryDetail({ slug }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getIndustryPage(slug)
      .then((p) => {
        setPage(p);
        document.title = `${p.title} | WebsiteTech.pro`;
        if (p.meta_description) {
          let m = document.querySelector('meta[name="description"]');
          if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
          m.content = p.meta_description;
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-24 text-center" data-testid="industry-notfound">
          <h1 className="font-heading text-3xl font-bold text-slate-900">Page not found</h1>
          <Link to="/industries" className="text-brand mt-4 inline-block">Browse industry playbooks</Link>
        </main>
        <Footer />
      </>
    );
  }
  if (!page) return <main className="min-h-screen grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></main>;

  return (
    <>
      <Navbar />
      <main className="pt-24" data-testid="industry-detail">
        <section className="relative overflow-hidden bg-[#0b1220] text-white">
          <div className="absolute -right-16 -top-16 w-72 h-72 bg-brand/30 blur-3xl rounded-full" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-200 bg-white/5 border border-blue-400/20 rounded-full px-3 py-1.5">
              <MapPin size={13} /> {page.industry} · {page.location}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-tight max-w-3xl">{page.title}</h1>
            {page.hero_subtitle && <p className="text-lg text-slate-300 mt-4 max-w-2xl">{page.hero_subtitle}</p>}
            <button onClick={() => setConsultOpen(true)} data-testid="industry-hero-cta"
              className="mt-8 inline-flex items-center gap-2 bg-white text-slate-900 font-semibold rounded-xl px-6 py-3.5 text-sm hover:bg-slate-100 transition-colors">
              {page.cta} <ArrowRight size={15} />
            </button>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">
          {page.intro && <p className="text-lg leading-relaxed text-slate-600 max-w-3xl">{page.intro}</p>}

          {/* Challenges */}
          {page.challenges?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><Target size={20} className="text-brand" /> The lead-generation challenges</h2>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {page.challenges.map((c, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 card-shadow" data-testid={`industry-challenge-${i}`}>
                    <h3 className="font-semibold text-slate-900">{c.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-500">{c.detail}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Funnel */}
          {page.funnel?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Your acquisition funnel</h2>
              <div className="mt-6 space-y-3">
                {page.funnel.map((f, i) => (
                  <div key={i} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-4" data-testid={`industry-funnel-${i}`}>
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/10 text-sm font-bold text-brand">{i + 1}</div>
                    <div>
                      <div className="font-semibold text-slate-900">{f.stage}</div>
                      <div className="text-sm text-slate-500">{f.action}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* KPIs */}
          {page.kpis?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 size={20} className="text-brand" /> KPIs we optimize</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {page.kpis.map((k, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`industry-kpi-${i}`}>
                    <div className="font-semibold text-slate-900">{k.metric}</div>
                    <div className="text-sm text-slate-500 mt-1">{k.why}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Services */}
          {page.services?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Services in this playbook</h2>
              <div className="mt-6 flex flex-wrap gap-3">
                {page.services.map((s) => (
                  <Link key={s.slug} to={s.url} data-testid={`industry-service-${s.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-brand hover:text-brand transition-colors">
                    <Sparkles size={14} /> {s.label} <ArrowUpRight size={14} />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* FAQs */}
          {page.faqs?.length > 0 && (
            <section>
              <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><HelpCircle size={20} className="text-brand" /> Frequently asked</h2>
              <div className="mt-6 space-y-4">
                {page.faqs.map((f, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`industry-faq-${i}`}>
                    <div className="font-semibold text-slate-900">{f.q}</div>
                    <div className="mt-1.5 text-sm text-slate-500">{f.a}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-3xl bg-slate-900 p-8 md:p-12 text-center text-white">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold">{page.cta}</h2>
            <p className="text-slate-300 mt-3 max-w-xl mx-auto">Get a tailored plan for {page.industry.toLowerCase()} in {page.location}.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button onClick={() => setConsultOpen(true)} className="btn-primary rounded-xl px-6 py-3.5 text-sm flex items-center gap-2">Request a plan <ArrowRight size={15} /></button>
              <button onClick={() => navigate("/growth-advisor")} className="border border-white/25 text-white rounded-xl px-6 py-3.5 text-sm flex items-center gap-2 hover:bg-white/10 transition-colors"><Sparkles size={15} /> Ask the AI Growth Advisor</button>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ConsultationDialog open={consultOpen} onOpenChange={setConsultOpen}
        context={{ service_interest: `${page.industry} — ${page.location}`, source: "Industry Page" }} />
    </>
  );
}

export default function IndustryPage() {
  const { slug } = useParams();
  return slug ? <IndustryDetail slug={slug} /> : <IndustryIndex />;
}
